import { leaveBreadcrumb, mergeTags, send, setUser, start } from "./state";
import type { Breadcrumb, ErrorContext, ErrorReport, ErrorReportingUser, Reporter, ScopeTags } from "./types";

/** Starts sending errors to `reporter`. Call once, before the root layout renders. */
export function initErrorReporting(options: { reporter: Reporter }): void {
  start(options.reporter);
}

/**
 * Reports an unexpected error. Accepts anything a `catch` can receive: a value
 * that is not an `Error` is turned into one, so Sentry groups it by message
 * rather than as an opaque non-error event.
 */
export function reportError(error: unknown, context: ErrorContext = {}): void {
  send(toReport(error, context));
}

/** Records a step (navigation, a press, a request) to give later errors their lead-up. */
export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  leaveBreadcrumb(breadcrumb);
}

/** Sets the user later errors are reported under. Call with `null` on sign-out. */
export function identify(user: ErrorReportingUser): void {
  setUser(user);
}

/**
 * Tags every later error, such as the selected tenant. Merged into the tags set
 * so far; a `null` value removes that tag. Tags passed to `reportError` win.
 */
export function setTags(tags: ScopeTags): void {
  mergeTags(tags);
}

function toReport(thrown: unknown, context: ErrorContext): ErrorReport {
  const tags = context.tags ?? {};
  const extra = context.extra ?? {};
  const groupBy = context.groupBy ?? [];
  if (thrown instanceof Error) {
    return { error: thrown, tags, extra, groupBy };
  }
  if (typeof thrown === "string") {
    return { error: new Error(thrown), tags, extra, groupBy };
  }
  // Error-like objects, such as API error bodies: the message names the
  // problem, and the object keeps the rest (status, code) for the event.
  if (typeof thrown === "object" && thrown !== null && "message" in thrown && typeof thrown.message === "string") {
    return { error: new Error(thrown.message), tags, extra: { ...extra, thrown }, groupBy };
  }
  return { error: new Error(`Non-Error value thrown: ${describe(thrown)}`), tags, extra, groupBy };
}

/** `String()` throws for objects without a prototype or with a throwing `toString`. */
function describe(value: unknown): string {
  try {
    return String(value);
  } catch {
    return "(unprintable)";
  }
}
